import { useState, useEffect } from 'react';
import moment from 'moment';

import { Button, Modal, Input, Select, Table, Typography, message } from "antd";
import { CloseOutlined, ExclamationCircleOutlined, FormOutlined, PlusOutlined } from '@ant-design/icons';

import { formatDate, handleDownload } from 'utils/helpers';
import { ErrorResponse } from 'utils/types';

import { DownloadMemberDocument, GetMemberDocument } from "store/eligibility/documents/actions";

import UploadDocumentModal from 'components/Eligibility/MemberEligibilityDetails/UploadDocumentModal';

import MemberDocumentsTabProps from './Proptypes';

const { Option } = Select;
const { confirm } = Modal;
const { Title } = Typography;

const MemberDocumentsTab = (props: MemberDocumentsTabProps) => {
  const [memberDocumentDataSource, setMemberDocumentDataSource] = useState<any[]>([]);
  const [filterMemberDocument, setFilterMemberDocument] = useState<any[]>([]);
  const [category, setCategory] = useState<any>();
  const [categoryList, setCategoryList] = useState([]); //very long to define
  const [searchValue, setSearchValue] = useState('');
  const [isUploadDocumentModalVisible, setIsUploadDocumentModalVisible] = useState(false);
  const [isUpdateCategoryModalVisible, setIsUpdateCategoryModalVisible] = useState(false);
  const [categoryCode, setCategoryCode] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [tags, setTags] = useState('');
  const [uploadedOn, setUploadedOn] = useState('');
  const [memberDocument, setMemberDocument] = useState({} as GetMemberDocument);
  const [dos, setDos] = useState(''); // placeholder to get and set dos from the upload document model

  const memberDocumentColumns = [
    {
      title: "Category",
      key: "RmiDocumentCategory",
      dataIndex: "RmiDocumentCategory",
      sorter: (a: any, b: any) => a.RmiDocumentCategory.localeCompare(b.RmiDocumentCategory),
    },
    {
      title: "Document Name",
      key: "RmiOrigFileName",
      dataIndex: "RmiOrigFileName",
      render: (_: number, record: any) => {
        const downloadDocumentParams = {
          MemberFilePath: record.RmiFilePath,
          MemberFileName: record.RmiFileName,
          MembMpiNo: record.RmiMembMpiNo //TODO: Need to test this download. Legacy code uses RmiUid when it's suppose to be RmiMembMpiNo
        }

        return <><a onClick={() => downloadMemberDocument(downloadDocumentParams)}>{record.RmiOrigFileName}</a></>
      },
      sorter: (a: any, b: any) => a.RmiOrigFileName.localeCompare(b.RmiOrigFileName),
    },
    {
      title: "Tags",
      key: "RmiDocumentTag",
      dataIndex: "RmiDocumentTag",
      render: (_: number, record: any) => record.RmiDocumentTag,
      sorter: (a: any, b: any) => a.RmiDocumentTag.localeCompare(b.RmiDocumentTag),
    },
    {
      title: "Uploaded On",
      key: "RmiUploadDate",
      dataIndex: "RmiUploadDate",
      render: (_: number, record: any) => formatDate(record.RmiUploadDate, 'MM/DD/YYYY [at] h:mmA'),
      sorter: (a: any, b: any) => a.RmiUploadDate.localeCompare(b.RmiUploadDate),
    },
    {
      title: (<>
        <Button type="primary" onClick={() => setIsUploadDocumentModalVisible(true)} icon={<PlusOutlined />}>Upload</Button>
      </>),
      key: "edit",
      dataIndex: "edit",
      render: (_: number, record: any) =>
        <>
          <Button onClick={() => openUpdateCategoryModal(record)} className="mr-2 btn" style={{background: 'orange', color: 'white', border: 'orange'}} icon={<FormOutlined />} size={"small"}></Button>
          { moment().subtract(1, 'd') < moment(record.RmiUploadDate) && ( <Button type="primary" onClick={() => deleteMemberDoc(record)} size={"small"} icon={<CloseOutlined />} danger></Button> )}
        </>
    },
  ];

  useEffect(() => {
    getCategory();
    if (props.activeTab === '3') getMemberDocuments();
  },[props.activeTab])


  const downloadMemberDocument = (data: DownloadMemberDocument) => {
    return props.downloadMemberDocument(data.MemberFilePath, data.MemberFileName, data.MembMpiNo)
      .then((response: any) => {
        handleDownload(response, data.MemberFileName);
      })
      .catch((err: ErrorResponse) => {
        message.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const getCategory = () => {
    const getCategoryParams = {
      "CompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "MembMpiNo": props.eligibilityMemberInfo.RmiMembMpiNo
    };

    return props.getCategory(getCategoryParams)
      .then((data: any) => { //too long to define
        setCategoryList(data);
      })
      .catch((err: ErrorResponse) => {
        message.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
      });
  }

  const deleteMemberDoc = (memberDocument: GetMemberDocument) => {
    const params = {
      "CompanyId": memberDocument.RmiCompanyId,
      "DocumentCategory": memberDocument.RmiCategoryCode,
      "MeasureCode": memberDocument.RmiMeasureCode,
      "MeasureGroup": memberDocument.RmiMeasureGroup,
      "MembMpiNo": memberDocument.RmiMembMpiNo,
      "MemberFileName": memberDocument.RmiFileName,
      "MemberFilePath": memberDocument.RmiFilePath,
      "ModifiedBy": memberDocument.RmiModifiedBy,
      "OrigFileName": memberDocument.RmiOrigFileName,
      "ProvKeyId": props.eligibilityMemberInfo.RmiProvKeyId,
      "Uid": memberDocument.RmiUid
    }

    confirm({
      title: 'Delete?',
      content: 'You will not be able to recover this file after it is deleted.',
      icon: <ExclamationCircleOutlined />,
      onOk() {
        props.deleteMemberDoc(params)
        .then(() => {
          message.success("Delete was successful.");
          getMemberDocuments();
        })
        .catch((err: ErrorResponse) => {
          message.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
        })
      }
    });
  }

  const openUpdateCategoryModal = (memberDocument: GetMemberDocument) => {
    const categorySelected = categoryList.find((category: any) => category.RmiCategoryCode === memberDocument.RmiDocumentCategory);
    setCategory(JSON.stringify(categorySelected));
    setCategoryCode(memberDocument.RmiDocumentCategory);
    setDocumentName(memberDocument.RmiOrigFileName);
    setTags(memberDocument.RmiDocumentTag);
    setUploadedOn(formatDate(memberDocument.RmiUploadDate, 'MM/DD/YYYY [at] h:mmA'));
    setIsUpdateCategoryModalVisible(true);
    setMemberDocument(memberDocument);
  }

  const updateCategory = () => {
    const categorySelected = JSON.parse(category);
    const fileNameArray = memberDocument.RmiFileName.split('_');
    const updateCategoryParams = {
      "CompanyId": memberDocument.RmiCompanyId,
      "DocumentCategory": categorySelected.RmiCategoryCode,
      "MeasureCode": categorySelected.RmiMeasureCode,
      "MeasureGroup": categorySelected.RmiMeasureGroup,
      "MembMpiNo": memberDocument.RmiMembMpiNo,
      "MemberFileName": memberDocument.RmiFileName,
      "MemberFilePath": memberDocument.RmiFilePath,
      "ModifiedBy": sessionStorage.user_id,
      "OrigFileName": memberDocument.RmiOrigFileName,
      "ProvKeyId": props.eligibilityMemberInfo.RmiProvKeyId,
      "Uid": memberDocument.RmiUid,
      "UpdatedMemberFileName": fileNameArray[0] + "_" + categorySelected.RmiCategoryCode + "_" + fileNameArray[2]
    }

    return props.updateCategory(updateCategoryParams)
      .then(() => {
        message.success('Member Document category updated!');
        getMemberDocuments();
      })
      .catch((err: ErrorResponse) => {
        // TODO - error message should be coming from the back-end api
        message.error(err.response?.data?.Message || 'The file was not updated. Please try again in a few minutes');
      });
  }

  const getMemberDocuments = () => {
    const memberInfoParams = {
      "CompanyId": props.eligibilityMemberInfo.RmiCompanyId,
      "MembMpiNo": props.eligibilityMemberInfo.RmiMembMpiNo
    };
    props.getMemberDocuments(memberInfoParams)
    .then((data: any[]) => {
      setMemberDocumentDataSource(data); //original array used to filter the documents
      setFilterMemberDocument(data); //data source used for the table
    })
    .catch((err: ErrorResponse) => {
      message.error(err.response?.data?.Message || 'Something went wrong, please contact a system administrator.');
    });
  }

  const isUploadDocumentModalVisibleHandler = () => {
    setIsUploadDocumentModalVisible(false);
    getMemberDocuments();
  }

  const handleCancel = () => {
    setCategory(null);
    setIsUpdateCategoryModalVisible(false);
  }
  const handleOk = () => {
    updateCategory();
    setIsUpdateCategoryModalVisible(false);
  }

  return (
    <>
      <div className="row">
        <div className="col">
          <Title className="ml-3" level={5}>Member Document</Title>
        </div>
      </div>
      <Input.Search
        placeholder="Search..."
        value={searchValue}
        onChange={(e) => {
          const currValue = e.target.value;
          setSearchValue(currValue);
          const filteredData = memberDocumentDataSource.filter(
            (entry: any) =>
              entry.RmiDocumentCategory.toLowerCase().includes(currValue.toLowerCase()) ||
              entry.RmiOrigFileName.toLowerCase().includes(currValue.toLowerCase()) ||
              entry.RmiDocumentTag.toLowerCase().includes(currValue.toLowerCase())
          );
          setFilterMemberDocument(filteredData);
        }}
      />
      <Table
        columns={memberDocumentColumns}
        dataSource={filterMemberDocument}
        pagination={{ position: ["bottomCenter"], hideOnSinglePage: true }}
        loading={props.isFetching}
      />
      <UploadDocumentModal
        claimDos={dos}
        setClaimDos={setDos}
        eligibilityMemberInfo={props.eligibilityMemberInfo}
        isUploadDocumentModalVisibleHandler={isUploadDocumentModalVisibleHandler}
        isUploadDocumentModalVisible={isUploadDocumentModalVisible}
        uploadFilestoAuth={props.uploadFilestoAuth}
        isUploading={props.isUploading}
        getCategory={props.getCategory}
        uploadMultipleCategories={true}
        allowMultipleAttachments={true}
      />
      <Modal
        visible={isUpdateCategoryModalVisible}
        width={1000}
        closable={false}
        maskClosable = {false}
        onCancel={handleCancel}
        onOk={handleOk}
        footer={[
          <Button
            key="cancel"
            onClick={handleCancel}
          >
            Cancel
          </Button>,
          <Button
            key="update"
            type="primary"
            form="attachmentForm"
            onClick={handleOk}
          >
            Update
          </Button>
        ]}
      >
        <div className="row">
          <div className="col-lg text-center">
            <Title level={3}>Update Category</Title>
          </div>
        </div>

        <hr/>

        <div className="row">
          <div className="col-lg">
            Category: {categoryCode}
          </div>
          <div className="col-lg">
            Tags: {tags}
          </div>
          <div className="col-lg">
            Uploaded On: {uploadedOn}
          </div>
        </div>
        <div className="row">
        <div className="col-lg">
            Document Name: {documentName}
          </div>
        </div>

        <div>
          <label><b>New Category: </b></label>
          <Select
            showSearch
            value={category}
            placeholder="SELECT A CATEGORY"
            onChange={value => { setCategory(value) }}
            style={{width: '100%'}}
            >
              {categoryList.map((option: any, key: number) => { //categoryList options too long to define
                return <Option key={key} value={JSON.stringify(option)}>{option.RmiCategoryDesc}</Option>;
              })}
          </Select>
        </div>
      </Modal>
    </>
  );
};
export default MemberDocumentsTab;
